import type { Task } from '../../types';
import { isSystemProject } from '../../utils/idNormalizer';
import {
  type CommTask,
  normalizePersonName,
  normalizeEmail,
  getVorgangColumn,
} from './personModel';

export interface PersonEntry {
  key: string;
  name: string;
  role?: string;
  organization?: string;
  contactEmail?: string;
  contactPhone?: string;
  lastContactDate?: string;
  /** All real contact records of this person (may span several projects in the global view). */
  records: CommTask[];
  /** The record used for editing (the first one; in project view: the project's record). */
  primary?: CommTask;
  projectIds: string[];
  projectNames: string[];
  /** True if the person only exists as an assignee name (no contact record yet). */
  synthetic: boolean;
  syntheticProjectId?: string;
  openVorgaenge: CommTask[];
}

export interface SyntheticPersonSeed {
  name: string;
  projectId: string;
  projectName?: string;
  role?: string;
  organization?: string;
}

/**
 * Resolves a stakeholder entry from `project.metadata.stakeholders` into a `SyntheticPersonSeed`.
 */
export function resolveStakeholderSeed(
  s: { name?: string; role?: string; organization?: string } | null | undefined,
  projectId: string,
  projectName?: string,
  knownPersonNames?: Set<string>
): SyntheticPersonSeed | null {
  const rawName = (s?.name || '').trim();
  if (!rawName) return null;

  const parenMatch = rawName.match(/^([^(]+)\(([^)]+)\)$/);
  if (parenMatch) {
    const outer = parenMatch[1].trim();
    const inner = parenMatch[2].trim();
    const innerNorm = normalizePersonName(inner);
    if (!innerNorm || innerNorm === 'zielgruppe' || innerNorm === 'all' || innerNorm === 'alle') {
      return null;
    }
    if (innerNorm.includes(' ') || (knownPersonNames && knownPersonNames.has(innerNorm))) {
      return {
        name: inner,
        projectId,
        projectName,
        role: s?.role,
        organization: s?.organization || outer,
      };
    }
  }

  return {
    name: rawName,
    projectId,
    projectName,
    role: s?.role,
    organization: s?.organization,
  };
}

interface Group {
  key: string;
  email: string;
  nameNorm: string;
  records: CommTask[];
  seeds: SyntheticPersonSeed[];
}

const laterDate = (a?: string, b?: string) => (!a ? b : !b ? a : a > b ? a : b);

/**
 * Builds the deduplicated person list.
 */
export function buildPersons(
  records: CommTask[],
  seeds: SyntheticPersonSeed[],
  vorgaenge: CommTask[]
): { persons: PersonEntry[]; personKeyOfVorgang: (v: Task) => string | undefined } {
  const groups = new Map<string, Group>();
  const byName = new Map<string, Group[]>();
  const recordGroup = new Map<string, Group>();

  const addToNameIndex = (g: Group) => {
    if (!g.nameNorm) return;
    const list = byName.get(g.nameNorm) || [];
    if (!list.includes(g)) list.push(g);
    byName.set(g.nameNorm, list);
  };

  records.forEach((r) => {
    const email = normalizeEmail(r.contactEmail);
    if (!email) return;
    const key = `e:${email}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        email,
        nameNorm: normalizePersonName(r.name || r.assignee),
        records: [],
        seeds: [],
      };
      groups.set(key, g);
    }
    g.records.push(r);
    addToNameIndex(g);
    recordGroup.set(String(r.globalId || r.id), g);
  });

  const findOrCreateNameGroup = (nameNorm: string): Group | undefined => {
    if (!nameNorm) return undefined;
    const candidates = byName.get(nameNorm) || [];
    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1) {
      const nameGroup = candidates.find((c) => !c.email);
      if (nameGroup) return nameGroup;
    }
    const key = `n:${nameNorm}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, email: '', nameNorm, records: [], seeds: [] };
      groups.set(key, g);
      addToNameIndex(g);
    }
    return g;
  };

  records.forEach((r) => {
    if (normalizeEmail(r.contactEmail)) return;
    const nameNorm = normalizePersonName(r.name || r.assignee);
    if (!nameNorm) {
      const hasPhone = Boolean((r.contactPhone || '').trim());
      const hasOrg = Boolean((r.organization || '').trim());
      const hasEmail = Boolean((r.contactEmail || '').trim());
      const hasRole = Boolean((r.role || '').trim());
      const hasNotes = Boolean(
        r.notes && (Array.isArray(r.notes) ? r.notes.length > 0 : String(r.notes).trim())
      );
      const isFresh = Boolean(r.isInlineNew);

      if (!hasPhone && !hasOrg && !hasEmail && !hasRole && !hasNotes && !isFresh) return;

      const key = `id:${r.globalId || r.id}`;
      const g: Group = { key, email: '', nameNorm: '', records: [r], seeds: [] };
      groups.set(key, g);
      recordGroup.set(String(r.globalId || r.id), g);
      return;
    }
    const g = findOrCreateNameGroup(nameNorm)!;
    g.records.push(r);
    recordGroup.set(String(r.globalId || r.id), g);
  });

  seeds.forEach((s) => {
    const nameNorm = normalizePersonName(s.name);
    if (!nameNorm) return;
    const g = findOrCreateNameGroup(nameNorm)!;
    g.seeds.push(s);
  });

  vorgaenge.forEach((v) => {
    const assigneeStr = (v.type === 'contact' ? v.name || v.assignee : v.assignee) || '';
    if (!assigneeStr.trim() || assigneeStr === 'all') return;
    assigneeStr
      .split(/[,;/]/)
      .map((a) => a.trim())
      .filter((a) => a && a !== 'all')
      .forEach((aName) => {
        const nameNorm = normalizePersonName(aName);
        if (!nameNorm) return;
        const g = findOrCreateNameGroup(nameNorm);
        if (g && g.records.length === 0 && g.seeds.length === 0) {
          g.seeds.push({
            name: aName,
            projectId: v.projectId || '',
            projectName: v.projectName,
            role: v.role,
            organization: v.organization,
          });
        }
      });
  });

  const recordById = new Map<string, Group>();
  records.forEach((r) => {
    const g = recordGroup.get(String(r.globalId || r.id));
    if (g) recordById.set(String(r.id), g);
  });

  const personKeyOfVorgang = (v: Task): string | undefined => {
    if (v.contactId !== undefined && v.contactId !== null && v.contactId !== '') {
      const g = recordById.get(String(v.contactId));
      if (g) return g.key;
    }
    if (v.personKey && groups.has(v.personKey)) {
      return v.personKey;
    }
    const nameNorm = normalizePersonName(
      v.type === 'contact' ? v.name || v.assignee : v.assignee
    );
    if (!nameNorm) return undefined;
    const candidates = byName.get(nameNorm) || [];
    if (candidates.length === 1) return candidates[0].key;
    const nameGroup = candidates.find((c) => !c.email);
    return nameGroup?.key || (candidates[0] ? candidates[0].key : undefined);
  };

  const openByKey = new Map<string, CommTask[]>();
  const allVorgaengeByKey = new Map<string, CommTask[]>();
  vorgaenge.forEach((v) => {
    const key = personKeyOfVorgang(v);
    if (!key) return;
    const allList = allVorgaengeByKey.get(key) || [];
    allList.push(v);
    allVorgaengeByKey.set(key, allList);

    const col = getVorgangColumn(v);
    if (col !== 'open' && col !== 'pending') return;
    const list = openByKey.get(key) || [];
    list.push(v);
    openByKey.set(key, list);
  });

  const persons: PersonEntry[] = [];
  groups.forEach((g) => {
    if (g.records.length === 0 && g.seeds.length === 0) return;
    const primary = g.records[0];
    const merged = g.records.reduce<Partial<PersonEntry>>(
      (acc, r) => ({
        name: acc.name || (r.name || r.assignee || '').trim(),
        role: acc.role || r.role,
        organization: acc.organization || r.organization,
        contactEmail: acc.contactEmail || r.contactEmail,
        contactPhone: acc.contactPhone || r.contactPhone,
        lastContactDate: laterDate(acc.lastContactDate, r.lastContactDate),
      }),
      {}
    );
    const seed = g.seeds[0];
    const personVorgaenge = allVorgaengeByKey.get(g.key) || [];
    const projectIds = Array.from(
      new Set(
        [
          ...g.records.map((r) => r.projectId || ''),
          ...g.records.flatMap((r) => r.projectIds || []),
          ...g.seeds.map((s) => s.projectId),
          ...personVorgaenge.map((v) => v.projectId || ''),
          ...personVorgaenge.flatMap((v) => v.projectIds || []),
        ].filter((id) => Boolean(id && !isSystemProject(id)))
      )
    );
    const projectNames = Array.from(
      new Set(
        [
          ...g.records.flatMap((r) => (r.projectName || '').split(',').map((s) => s.trim())),
          ...g.seeds.flatMap((s) => (s.projectName || '').split(',').map((str) => str.trim())),
          ...personVorgaenge.flatMap((v) => (v.projectName || '').split(',').map((s) => s.trim())),
        ].filter((n) =>
          Boolean(
            n &&
              !['Allgemein', 'Kommunikation', 'Nicht zugeordnet', 'Posteingang', 'Inbox'].includes(
                n
              )
          )
        )
      )
    );

    const displayName =
      merged.name ||
      seed?.name ||
      (primary?.name || primary?.assignee || '').trim() ||
      (primary?.role ? primary.role : '') ||
      'Neue Person';

    persons.push({
      key: g.key,
      name: displayName,
      role: merged.role || seed?.role,
      organization: merged.organization || seed?.organization,
      contactEmail: merged.contactEmail,
      contactPhone: merged.contactPhone,
      lastContactDate: merged.lastContactDate,
      records: g.records,
      primary,
      projectIds,
      projectNames,
      synthetic: g.records.length === 0,
      syntheticProjectId: g.records.length === 0 ? seed?.projectId : undefined,
      openVorgaenge: openByKey.get(g.key) || [],
    });
  });

  persons.sort((a, b) => {
    const aIsUnnamed = !a.name || a.name === 'Neue Person';
    const bIsUnnamed = !b.name || b.name === 'Neue Person';
    if (aIsUnnamed && !bIsUnnamed) return -1;
    if (!aIsUnnamed && bIsUnnamed) return 1;
    return a.name.localeCompare(b.name, 'de');
  });

  return { persons, personKeyOfVorgang };
}

export interface SidebarPersonSummaryInfo {
  personKey?: string;
  name: string;
  role?: string;
  organization?: string;
  email?: string;
  phone?: string;
  taskCount: number;
  activeVorgangCount: number;
  hasActiveVorgang: boolean;
  hasOverdue: boolean;
}

export function buildSidebarPeopleSummaryMap(
  scopedPersons: PersonEntry[],
  taskPool: Task[],
  todayISO: string
): { sidebarPeople: string[]; sidebarPeopleData: Map<string, SidebarPersonSummaryInfo> } {
  const sidebarPeopleData = new Map<string, SidebarPersonSummaryInfo>();

  // Pre-index taskPool in O(T) once by normalized assignee and contactId
  const tasksByAssignee = new Map<string, Task[]>();
  const tasksByContactId = new Map<string, Task[]>();

  for (const t of taskPool) {
    if (t.archived) continue;
    if (t.assignee) {
      const parts = t.assignee.split(/[,;/]+/);
      for (const part of parts) {
        const norm = normalizePersonName(part);
        if (norm) {
          let list = tasksByAssignee.get(norm);
          if (!list) {
            list = [];
            tasksByAssignee.set(norm, list);
          }
          list.push(t);
        }
      }
    }
    if (t.contactId) {
      const cid = String(t.contactId);
      let list = tasksByContactId.get(cid);
      if (!list) {
        list = [];
        tasksByContactId.set(cid, list);
      }
      list.push(t);
    }
  }

  scopedPersons.forEach((p) => {
    const norm = normalizePersonName(p.name);
    let taskCount = 0;
    const activeVorgangCount = p.openVorgaenge.length;
    let hasOverdue = false;

    // Collect deduplicated tasks for this person in O(matched)
    const matchingTasks = new Set<Task>();
    if (norm) {
      const byA = tasksByAssignee.get(norm);
      if (byA) {
        for (const t of byA) matchingTasks.add(t);
      }
    }
    for (const r of p.records) {
      const byC = tasksByContactId.get(String(r.id));
      if (byC) {
        for (const t of byC) matchingTasks.add(t);
      }
    }

    matchingTasks.forEach((t) => {
      if (!t.completed && t.status !== 'completed') {
        if (t.type !== 'contact' && t.status !== 'pool') {
          taskCount++;
        }
        if (t.end && t.end < todayISO) {
          hasOverdue = true;
        }
      }
    });

    p.openVorgaenge.forEach((v) => {
      if (v.end && v.end < todayISO) {
        hasOverdue = true;
      }
    });

    sidebarPeopleData.set(p.name, {
      personKey: p.key,
      name: p.name,
      role: p.role,
      organization: p.organization,
      email: p.contactEmail,
      phone: p.contactPhone,
      taskCount,
      activeVorgangCount,
      hasActiveVorgang: activeVorgangCount > 0,
      hasOverdue,
    });
  });

  const sidebarPeople = Array.from(sidebarPeopleData.keys()).sort((a, b) =>
    a.localeCompare(b, 'de')
  );

  return { sidebarPeople, sidebarPeopleData };
}
